import { Megaphone, Info, CircleCheck, TriangleAlert, OctagonAlert } from 'lucide-react'
import type { HelpBannerTone } from '@/pages/HelpCenter/types'

interface BannerPreviewProps {
  title: string
  bodyHtml: string
  tone: HelpBannerTone
  accentColor: string | null
  brandPrimary: string | null
}

// Mirrors _banners.html.erb + the .hc-banner rules in help_center.css. Kept as
// a hand-rolled copy rather than importing the public stylesheet: the admin app
// has no --hc-* tokens, so the same CSS would render colourless here.
const TONE_COLORS: Record<Exclude<HelpBannerTone, 'brand'>, string> = {
  neutral: '#8b8b8b',
  info: '#3b82f6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
}

const TONE_ICONS = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: OctagonAlert,
} as const

export function BannerPreview({ title, bodyHtml, tone, accentColor, brandPrimary }: BannerPreviewProps) {
  const accent = accentColor ?? (tone === 'brand' ? brandPrimary ?? '#10b981' : TONE_COLORS[tone])
  const Icon = (!accentColor && tone in TONE_ICONS ? TONE_ICONS[tone as keyof typeof TONE_ICONS] : Megaphone)

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm"
      style={{ borderLeft: `3px solid ${accent}`, background: `color-mix(in srgb, ${accent} 7%, var(--card))` }}
    >
      <Icon className="mt-0.5 size-4 shrink-0" style={{ color: accent }} />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {bodyHtml
          ? (
            <div
              className="[&_a]:text-[var(--banner-accent)] [&_a]:underline [&_a]:underline-offset-2 [&_p]:m-0 [&_p+p]:mt-1"
              style={{ ['--banner-accent' as string]: accent }}
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
            />
          )
          : <p className="text-muted-foreground">Your message appears here.</p>}
      </div>
    </div>
  )
}
